import { differenceInCalendarDays } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { ActionSummary } from "features/Resources/Shared/models";
import { NewHireJourney } from "models/v2/newHireJourney";
import { User } from "features/User/models";
import ROLE from "models/role";
import { dayDifference } from "helpers/time";

// Start date counts as Day 1, so add 1 to the diff. Returns negative values
// for preboarding (before start) and null when start date isn't set yet.
// When timezone is provided, computes calendar-day diff in the new hire's
// timezone so a viewer in a different tz sees the correct day near midnight.
export function getJourneyDayNumber(startDate: Date | undefined, timezone?: string | null): number | null {
  if (!startDate) return null;
  const now = timezone ? new TZDate(new Date(), timezone) : new Date();
  const start = timezone ? new TZDate(new Date(startDate), timezone) : new Date(startDate);
  return differenceInCalendarDays(now, start) + 1;
}

export type JourneyViewerRole = "manager" | "buddy" | "people_team_contact" | "recruiter" | "it_team_contact";

const ROLE_PRIORITY: JourneyViewerRole[] = [
  "manager",
  "people_team_contact",
  "buddy",
  "recruiter",
  "it_team_contact",
];

const ROLE_LABEL: Record<JourneyViewerRole, string> = {
  manager: "manager",
  buddy: "onboarding buddy",
  people_team_contact: "people team contact",
  recruiter: "recruiter",
  it_team_contact: "IT contact",
};

/** Roles the current viewer holds on this journey (manager/buddy/etc.).
 *  Returns an empty array if the viewer has no direct relationship — e.g. a
 *  people-team admin who isn't the assigned contact on this journey.
 */
export function viewerRolesOnJourney(
  currentUser: User | undefined,
  journey: NewHireJourney,
): JourneyViewerRole[] {
  if (!currentUser) return [];
  const roles: JourneyViewerRole[] = [];
  if (journey.manager?.id === currentUser.id) roles.push("manager");
  if (journey.buddy?.id === currentUser.id) roles.push("buddy");
  if (journey.peopleTeamContact?.id === currentUser.id) roles.push("people_team_contact");
  if (journey.recruiter?.id === currentUser.id) roles.push("recruiter");
  if (journey.itTeamContact?.id === currentUser.id) roles.push("it_team_contact");
  return ROLE_PRIORITY.filter((r) => roles.includes(r));
}

/** "You're Carter's onboarding buddy" */
export function viewerRoleSentence(role: JourneyViewerRole, newHireFirstName: string | undefined): string {
  const name = newHireFirstName || "their";
  return `You're ${name}'s ${ROLE_LABEL[role]}`;
}

/** Compact display label for a role tag: "Manager", "Buddy", "People contact", etc. */
export function viewerRoleLabel(role: JourneyViewerRole): string {
  return SHORT_ROLE_LABEL[role];
}

const SHORT_ROLE_LABEL: Record<JourneyViewerRole, string> = {
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "People contact",
  recruiter: "Recruiter",
  it_team_contact: "IT contact",
};

const DAYS_IN_MONTH = 30;
const DAYS_IN_90_PERIOD = 90;
const DAYS_IN_6_MONTHS = 180;
const DAYS_IN_YEAR = 365;

function actionHasStarted(action: ActionSummary) {
  return !!action?.action?.attemptedAt;
}

export default function haveAutomationsStarted(actions: ActionSummary[]) {
  return actions ? actions.some((action) => actionHasStarted(action)) : false;
}

export function calculateDaysSinceStart(startDate: Date | string | undefined): number {
  if (!startDate) return 0;
  const today = new Date();
  const days = dayDifference(startDate, today);
  return days !== null ? days + 1 : 0; // +1 to include start day
}

export function calculatePercentage(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

export function getOnboardingPeriodDisplay(daysCompleted: number) {
  if (daysCompleted <= 0) {
    const daysUntilStart = Math.abs(daysCompleted);
    const timeLabel = daysUntilStart === 0
      ? "Starting today"
      : `Starts in ${daysUntilStart} day${daysUntilStart !== 1 ? "s" : ""}`;
    return { timeLabel, progressLabel: "Pre-boarding", progress: 0 };
  }
  if (daysCompleted > DAYS_IN_YEAR) {
    return {
      timeLabel: "1 Year has passed! 🎉",
      progressLabel: "1 Year Progress",
      progress: 100,
    };
  }

  let timeLabel: string;
  if (daysCompleted > DAYS_IN_MONTH) {
    const monthNum = Math.ceil(daysCompleted / DAYS_IN_MONTH);
    timeLabel = `Day ${daysCompleted} (Month ${monthNum})`;
  } else if (daysCompleted > 14) {
    const weekNum = Math.floor(daysCompleted / 7);
    timeLabel = `Day ${daysCompleted} (Week ${weekNum})`;
  } else {
    timeLabel = `Day ${daysCompleted}`;
  }

  if (daysCompleted > DAYS_IN_6_MONTHS) {
    return {
      timeLabel,
      progressLabel: "1 Year Progress",
      progress: Math.round((daysCompleted / DAYS_IN_YEAR) * 100),
    };
  }
  if (daysCompleted > DAYS_IN_90_PERIOD) {
    return {
      timeLabel,
      progressLabel: "6 Month Progress",
      progress: Math.round((daysCompleted / DAYS_IN_6_MONTHS) * 100),
    };
  }
  return {
    timeLabel,
    progressLabel: "90 Day Progress",
    progress: Math.round((daysCompleted / DAYS_IN_90_PERIOD) * 100),
  };
}

const CLOSABLE_STATUSES: NewHireJourney["status"][] = ["upcoming", "active"];

export function canCloseJourney(
  journey: Pick<NewHireJourney, "status">,
  currentUser: Pick<User, "permission"> | undefined,
): boolean {
  if (!CLOSABLE_STATUSES.includes(journey.status)) return false;
  return (currentUser?.permission ?? []).some((r) =>
    ([ROLE.PEOPLE_TEAM, ROLE.COMPANY_ADMIN] as string[]).includes(r),
  );
}
